import React, { useRef } from 'react'
import { useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import Rails from '@rails/ujs'
import InputText from '../form/InputText'
import InputFile from '../form/InputFile'
import InputColor from '../form/InputColor'
import LanguageTable from './LanguageTable'


const FILE_MAX_SIZE = 65535 /// bytes
const FILE_TO_BIG = 'The file size is over 64KB'
const MAX_FILE_SIZE = 'Maximum file size is 64KB'

const defaultColorScheme = {
    '--brand-300': '#1fa2ff',
    '--brand-400': '#0189e9',
    '--color-brand-gray-dark-900': '#191919',
}

function parseFileName(assetUrl) {
    if (assetUrl) {
        const end = assetUrl.lastIndexOf('?t=')
        return assetUrl.substring(assetUrl.lastIndexOf('/') + 1, end === -1 ? undefined : end)
    }
    return ''
}

function validateFileSize(files) {
    for (const f of files) {
        if (f.size >= FILE_MAX_SIZE) {
            return false
        }
    }
    return true
}

function AppearanceConfig(props) {
    const defaultValues = {
        logoLightFileName: parseFileName(props.config?.tsp?.logo?.light),
        logoDarkFileName: parseFileName(props.config?.tsp?.logo?.dark),
        faviconFileName: parseFileName(props.config?.tsp?.faviconUrl),
        ...defaultColorScheme,
        ...props.config?.tsp?.colorScheme,
        pageTitle: props.config?.tsp?.pageTitle || '',
        supportedLanguages: props.config?.tsp?.supportedLanguages
    }
    const { register, errors, trigger, watch } = useForm({ defaultValues })
    const values = watch()

    const formRef = useRef()

    const handleSubmit = async (e) => {
        if (await trigger()) {
            formRef.current.submit()
            e.target.disabled = true
        }
    }

    return <form action={`${props.action}`} method='POST' encType="multipart/form-data"
                 ref={formRef}
                 className='flex flex-col p-2 font-light wizard-step-collapse-animation' autoComplete='off'>
        <input type='hidden' name='authenticity_token' value={Rails.csrfToken()}/>
        <InputText name='pageTitle'
                   label='Application name'
                   ref={register()}
                   error={errors?.appName}
        />
        <InputFile name='logoLight'
                   label='Logo (light)'
                   fileName={(values.logoLight && values.logoLight.length > 0) ? values.logoLight[0].name : defaultValues.logoLightFileName}
                   ref={register({
                       validate: { file: v => validateFileSize(v) || <p>{FILE_TO_BIG}</p> }
                   })}
                   description={MAX_FILE_SIZE}
                   error={errors?.logoLight}
        />
        <InputFile name='logoDark'
                   label='Logo (dark)'
                   fileName={(values.logoDark && values.logoDark.length > 0) ? values.logoDark[0].name : defaultValues.logoDarkFileName}
                   ref={register()}
                   description={MAX_FILE_SIZE}
                   error={errors?.logoDark}
        />
        <InputFile name='faviconUrl'
                   label='Favicon'
                   fileName={(values.faviconUrl && values.faviconUrl.length > 0) ? values.faviconUrl[0].name : defaultValues.faviconFileName}
                   ref={register()}
                   description={MAX_FILE_SIZE}
                   error={errors?.faviconUrl}
        />
        <LanguageTable name='supportedLanguages'
                       ref={register()}
                       data={defaultValues.supportedLanguages}
                       tableLabel='Supported languages'
                       dropdownIcon={props.dropdownIcon}/>
        <label>Brand colors</label>
        <div className='w-full text-center'>
            <table className="table-auto w-full text-left">
                <thead className="bg-dark-blue-700 mb-2">
                <tr>
                    <th className='font-thin text-sm pl-4' style={{ width: '34%' }}>Variable</th>
                    <th className='font-thin text-sm pl-4' style={{ width: '33%' }}>Default</th>
                    <th className='font-thin text-sm pl-4' style={{ width: '33%' }}>Custom</th>
                </tr>
                </thead>
                <tbody>
                <tr className='hover:bg-dark-blue-700'>
                    <td className='pl-4'>--brand-300</td>
                    <td className='pl-4'><InputColor name='--brand-300-def' value={defaultColorScheme['--brand-300']}
                                    label={defaultColorScheme['--brand-300']} disabled/></td>
                    <td className='pl-4'><InputColor name='--brand-300' label={values['--brand-300']} ref={register()}/></td>
                </tr>
                <tr className='hover:bg-dark-blue-700'>
                    <td className='pl-4'>--brand-400</td>
                    <td className='pl-4'><InputColor name='--brand-300-def' value={defaultColorScheme['--brand-400']}
                                    label={defaultColorScheme['--brand-400']} disabled/></td>
                    <td className='pl-4'><InputColor name='--brand-400' label={values['--brand-400']} ref={register()}/></td>
                </tr>
                <tr className='hover:bg-dark-blue-700'>
                    <td className='pl-4'>--brand-dark-900</td>
                    <td className='pl-4'><InputColor name='--color-brand-gray-dark-900-def' value={defaultColorScheme['--color-brand-gray-dark-900']}
                                    label={defaultColorScheme['--color-brand-gray-dark-900']} disabled/></td>
                    <td className='pl-4'><InputColor name='--color-brand-gray-dark-900' label={values['--color-brand-gray-dark-900']} ref={register()}/></td>
                </tr>

                </tbody>
            </table>
        </div>
        <div className={'flex flex-row-reverse pt-4'}>
            <button type='submit' onClick={(e) => {
                e.preventDefault()
                handleSubmit(e)
            }} className='btn-yellow w-38'>Save
            </button>
        </div>
    </form>
}

AppearanceConfig.propTypes = {
    action: PropTypes.string,
    config: PropTypes.object
}

export default AppearanceConfig
